import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type {
    AxisConfiguration,
    AxisPreviewItem,
    ShowTopStats,
} from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import {
    buildAxisPreviewItemsFromRows,
    extractShowTopStatsFromChartResponse,
} from 'components/pages/PackArchitecture/packArchitectureChart.helpers';
import { buildAxisPreviewRequest } from 'components/pages/PackArchitecture/packArchitectureChartRequest.helpers';
import { AXIS_PREVIEW_SALES_KPI_IDS } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import { PPA_AXIS_PREVIEW_QUERY_OPTIONS, fetchPpaChart, getPpaChartQueryKey } from 'hooks/ppaChartQuery.helpers';

const SHOW_TOP_PREVIEW_DEBOUNCE_MS = 400;

type PreviewFetchSnapshot = Pick<
    AxisConfiguration,
    'sortBy' | 'sortOrder' | 'showTop' | 'groupRestAsOther' | 'showOwnVendorFirst'
>;

type AxisPreviewQueryResult = {
    previewItems: AxisPreviewItem[];
    showTopStats: ShowTopStats | null;
};

type UseAxisConfigurationPreviewParams = {
    open: boolean;
    chartRequest: PricePackArchitectureRequest | null;
    xAxis: string;
    previewConfig: AxisConfiguration;
    /** When true, skips preview fetch (e.g. Show Top input empty or above max). */
    pauseFetch?: boolean;
};

const toFetchSnapshot = (config: AxisConfiguration): PreviewFetchSnapshot => ({
    sortBy: config.sortBy,
    sortOrder: config.sortOrder,
    showTop: config.showTop,
    groupRestAsOther: config.groupRestAsOther,
    showOwnVendorFirst: config.showOwnVendorFirst,
});

const hasNonShowTopChange = (previous: PreviewFetchSnapshot | null, current: PreviewFetchSnapshot) =>
    !previous ||
    previous.sortBy !== current.sortBy ||
    previous.sortOrder !== current.sortOrder ||
    previous.groupRestAsOther !== current.groupRestAsOther ||
    previous.showOwnVendorFirst !== current.showOwnVendorFirst;

export const useAxisConfigurationPreview = ({
    open,
    chartRequest,
    xAxis,
    previewConfig,
    pauseFetch = false,
}: UseAxisConfigurationPreviewParams) => {
    const [debouncedConfig, setDebouncedConfig] = useState(previewConfig);
    const previousSnapshotRef = useRef<PreviewFetchSnapshot | null>(null);

    useEffect(() => {
        if (!open) {
            previousSnapshotRef.current = null;
            return undefined;
        }

        const currentSnapshot = toFetchSnapshot(previewConfig);
        const fetchDelay = hasNonShowTopChange(previousSnapshotRef.current, currentSnapshot)
            ? 0
            : SHOW_TOP_PREVIEW_DEBOUNCE_MS;
        previousSnapshotRef.current = currentSnapshot;

        const timer = window.setTimeout(() => {
            setDebouncedConfig(previewConfig);
        }, fetchDelay);

        return () => {
            window.clearTimeout(timer);
        };
    }, [open, previewConfig]);

    const previewRequest = useMemo(() => {
        if (!chartRequest || !open || debouncedConfig.showTop <= 0) {
            return null;
        }
        return buildAxisPreviewRequest(chartRequest, debouncedConfig);
    }, [chartRequest, debouncedConfig, open]);

    const queryEnabled = open && Boolean(previewRequest) && !pauseFetch;

    const { data, isPending, isFetching } = useQuery<AxisPreviewQueryResult>({
        queryKey: previewRequest ? getPpaChartQueryKey(previewRequest) : ['ppa-axis-preview', 'disabled'],
        queryFn: async ({ signal }) => {
            const response = await fetchPpaChart(previewRequest!, signal);
            const rows = response.data ?? [];
            return {
                previewItems: buildAxisPreviewItemsFromRows(rows, xAxis, AXIS_PREVIEW_SALES_KPI_IDS),
                showTopStats: extractShowTopStatsFromChartResponse(response),
            };
        },
        enabled: queryEnabled,
        placeholderData: keepPreviousData,
        ...PPA_AXIS_PREVIEW_QUERY_OPTIONS,
    });

    const isConfigDebouncing =
        open && JSON.stringify(toFetchSnapshot(previewConfig)) !== JSON.stringify(toFetchSnapshot(debouncedConfig));

    const isLoading = queryEnabled && (isPending || isFetching || isConfigDebouncing);

    return {
        previewItems: open ? data?.previewItems ?? [] : [],
        showTopStats: open ? data?.showTopStats ?? null : null,
        isLoading,
    };
};
